import { Button, FormControl } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import Page from "~/components/Page";
import TableControls from "~/components/TableControls";
import { useTrans } from '~/hooks/useTrans';
import { apiGetCatalogs } from "~/providers/api";
import { ICatalogItem } from "~/providers/interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import AdminCatalogsTable from "~/tables/AdminCatalogsTable";
import { IAdminCatalogsFilter } from "~/providers/admin-interfaces";
import { ADMIN_CATALOGS_SORT_KEYS, PAGE_SIZE_ADMIN_CATALOGS } from "~/const";
import AdminCatalogsFilter from "~/components/AdminCatalogsFilter";
import MyPagination from "~/mui/MyPagination";

import "./style.less";

interface IState {
	loading: boolean;
	items: Array<ICatalogItem>;
	errorMsg: string;
	catalogsCount: number;
	filterData: null | IAdminCatalogsFilter;
}

const INIT_STATE: IState = {
	loading: true,
	items: [],
	errorMsg: "",
	catalogsCount: 0,
	filterData: null,
};

const INIT_STATE_FILTER: IAdminCatalogsFilter = {
	offset: 0,
	limit: PAGE_SIZE_ADMIN_CATALOGS,
	sortByLastUpdate: "DESC",
};

export default function AdminCatalogsPage() {
	const { trans, filterCatalogsDesc } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		...INIT_STATE,
	});
	const { state: stateFilter, setState: setStateFilter, updateState: updateStateFilter } = myUseState<IAdminCatalogsFilter>({
		...INIT_STATE_FILTER,
	});

	async function loadData(filter: IAdminCatalogsFilter = stateFilter) {
		updateState({
			loading: true,
			items: [],
			errorMsg: "",
		});

		const catalogsData = await apiGetCatalogs(filter, lang);

		updateState({
			loading: false,
			...catalogsData.noErrors
				? {
					items: catalogsData.data,
					catalogsCount: catalogsData.requestData.pagination.total,
				}
				: {},
			errorMsg: catalogsData.errors.join(", "),
		});
		catalogsData.noErrors && updateStateFilter({
			offset: catalogsData.requestData.pagination.offset,
		});
	}

	function hasFilters() {
		return Object.keys(stateFilter).length > Object.keys(INIT_STATE_FILTER).length;
	}

	function toggleFilters() {
		const filterData = state.filterData
			? null
			: {
				...stateFilter,
			};

		updateState({
			filterData,
		});
	}

	function setFilterAndLoadData(newFilter: IAdminCatalogsFilter) {
		setStateFilter(newFilter);
		loadData(newFilter);
	}

	function onSetFilters(newFilter: IAdminCatalogsFilter) {
		updateState({
			filterData: null,
		});
		setFilterAndLoadData(newFilter);
	}

	function clearFilters() {
		updateState({
			filterData: null,
		});
		setFilterAndLoadData({
			...INIT_STATE_FILTER,
		});
	}

	function onTableSortUpdate(newFilter: IAdminCatalogsFilter) {
		const curFilter: IAdminCatalogsFilter = {
			...stateFilter,
			offset: 0,
		};

		ADMIN_CATALOGS_SORT_KEYS.forEach(sort => {
			if (sort in curFilter) {
				delete curFilter[sort];
			}
		});

		setFilterAndLoadData({
			...curFilter,
			...newFilter,
		});
	}

	function onPageChange(page: number) {
		const offset = (page - 1) * PAGE_SIZE_ADMIN_CATALOGS;

		setFilterAndLoadData({
			...stateFilter,
			offset,
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page title={trans("menu.adminCatalogs")} breadcrumbs={[trans("administration"), trans("menu.adminCatalogs")]} checkInternalRole="salesman" onLoad={onPageLoad} isDirty={false} seoTitleTrans="seo.title.admin.catalog">
		<TableControls className="adminCatalogPage__tableControls">
			<AdminCatalogsFilter config={state.filterData} onSetFilters={onSetFilters}>
				<div className="adminCatalogPage__filterCont">
					<FormControl size="small">
						<Button color="success" variant="contained" onClick={toggleFilters} className="adminProjectsPage__preBtn btn-with-icon">
							{ state.filterData ? <CloseIcon /> : <SearchIcon /> } { state.filterData ? trans("allProjectsPage.closeFilters") : trans("allProjectsPage.editFilters") }
						</Button>
					</FormControl>
					{ hasFilters() && <FormControl size="small">
						<Button color="success" variant="contained" onClick={clearFilters} className="adminProjectsPage__preBtn btn-with-icon">
							<DeleteIcon /> { trans("allProjectsPage.clearFilters") }
						</Button>
					</FormControl> }
					<span>
						{ filterCatalogsDesc(stateFilter, state.catalogsCount) }
					</span>
				</div>
			</AdminCatalogsFilter>
		</TableControls>
		<AdminCatalogsTable items={state.items} filter={stateFilter} loading={state.loading} isCzech={isCzech} onSortUpdate={onTableSortUpdate} />
		{ !state.loading && <MyPagination offset={stateFilter.offset} perPage={PAGE_SIZE_ADMIN_CATALOGS} total={state.catalogsCount} onPageChange={onPageChange} /> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
	</Page>;
}
